import {lazy} from 'react';
import {type RouteObject} from 'react-router-dom';

import {ProtectedRoute} from './ProtectedRoute';
import AdminPortalPage from '../modules/adminPortal/pages/AdminPortal';
import {
  adminPortalRoles,
  ASSESSORS_READ,
  CERTIFICATE_READ,
  COHORTS_READ,
  FACILITATORS_READ,
  LEARNERS_READ,
  LEARNER_ENROLLMENTS_READ,
  MODERATORS_READ,
  OPTIX_ACADEMY_ADMIN_READ,
  QUALIFICATIONS_READ,
  SCORM_READ,
  SCORM_TEMPLATE_READ,
  SKILLS_PROGRAMS_READ,
  TEMPLATES_READ,
  TRAINING_COURSE_READ,
  TRAINME_COURSES_READ,
  TRAINME_OPTIX_ASSESSMENT_READ,
  USER_PROFILE_SUMMARY_READ,
  USER_READ,
} from '../modules/common/constants/roles';
import {routes} from '../modules/common/constants/routes';
import {
  type Breadcrumb,
  breadcrumbLoaderFunction,
} from '../modules/common/stores/breadcrumbStore';

const AdminPortalSCORM = lazy(
  async () => await import('../modules/adminPortal/pages/SCORMManagement'),
);
const AdminPortalScormTemplates = lazy(
  async () =>
    await import('../modules/adminPortal/pages/ScormTemplatesManagement'),
);
const AdminPortalCourses = lazy(
  async () => await import('../modules/adminPortal/pages/CourseManagement'),
);
const AdminPortalSkillsProgrammes = lazy(
  async () =>
    await import('../modules/adminPortal/pages/SkillProgrammeManagement'),
);
const AdminPortalSkillsProgrammeTemplates = lazy(
  async () =>
    await import(
      '../modules/adminPortal/pages/SkillsProgramTemplatesManagement'
    ),
);
const AdminPortalQualifications = lazy(
  async () =>
    await import('../modules/adminPortal/pages/QualificationManagement'),
);
const AdminPortalQualificationTemplates = lazy(
  async () =>
    await import(
      '../modules/adminPortal/pages/QualificationTemplatesManagement'
    ),
);
const AdminPortalUsers = lazy(
  async () => await import('../modules/adminPortal/pages/UserManagement'),
);
const AdminPortalEnrollmentManagement = lazy(
  async () => await import('../modules/adminPortal/pages/EnrollmentManagement'),
);
const AdminPortalCreateEnrollments = lazy(
  async () =>
    await import('../modules/adminPortal/pages/CreateEnrollmentsPage'),
);
const AdminPortalCertificates = lazy(
  async () =>
    await import('../modules/adminPortal/pages/CertificateManagement'),
);
const AdminPortalCohortManagement = lazy(
  async () => await import('../modules/adminPortal/pages/CohortManagement'),
);
const AdminPortalCohortDetail = lazy(
  async () => await import('../modules/adminPortal/pages/CohortDetail'),
);
const AdminPortalUserDetail = lazy(
  async () => await import('../modules/adminPortal/pages/UserDetail'),
);
const AdminPortalAssessments = lazy(
  async () => await import('../modules/adminPortal/pages/AssessmentManagement'),
);
const AdminPortalCreateAssessment = lazy(
  async () => await import('../modules/adminPortal/pages/CreateAssessment'),
);
const AdminPortalEditAssessment = lazy(
  async () => await import('../modules/adminPortal/pages/EditAssessment'),
);
const AdminPortalAssessmentTemplates = lazy(
  async () =>
    await import('../modules/adminPortal/pages/AssessmentTemplatesManagement'),
);
const AdminPortalAssessmentTemplateDetail = lazy(
  async () =>
    await import('../modules/adminPortal/pages/AssessmentTemplateDetail'),
);
const AdminPortalCreateAssessmentTemplate = lazy(
  async () =>
    await import('../modules/adminPortal/pages/CreateAssessmentTemplate'),
);
const AdminPortalEditAssessmentTemplate = lazy(
  async () =>
    await import('../modules/adminPortal/pages/EditAssessmentTemplate'),
);
const AdminPortalCourseModules = lazy(
  async () => await import('../modules/adminPortal/pages/CourseModules'),
);
const AdminPortalModuleManagement = lazy(
  async () => await import('../modules/adminPortal/pages/ModuleManagement'),
);
const AdminPortalStandUpTasks = lazy(
  async () => await import('../modules/adminPortal/pages/StandUpTasks'),
);

const baseAdminPortalBreadcrumbs: Breadcrumb[] = [
  {label: 'Admin Portal', path: routes.adminPortal.get()},
];

export const adminPortalChildren: RouteObject[] = [
  {
    path: '',
    element: (
      <ProtectedRoute roles={adminPortalRoles}>
        <AdminPortalPage />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([{label: 'Admin Portal'}]),
  },
  {
    path: routes.adminPortal.scorm,
    element: (
      <ProtectedRoute roles={[OPTIX_ACADEMY_ADMIN_READ, SCORM_READ]}>
        <AdminPortalSCORM />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'SCORM Packages'},
      {label: 'Org-Scoped'},
    ]),
  },
  {
    path: routes.adminPortal.scormTemplates,
    element: (
      <ProtectedRoute roles={[OPTIX_ACADEMY_ADMIN_READ, SCORM_TEMPLATE_READ]}>
        <AdminPortalScormTemplates />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'SCORM Packages'},
      {label: 'Templates'},
    ]),
  },
  {
    path: routes.adminPortal.courses,
    element: (
      <ProtectedRoute roles={[OPTIX_ACADEMY_ADMIN_READ, TRAINING_COURSE_READ]}>
        <AdminPortalCourses />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'Courses'},
    ]),
  },
  {
    path: routes.adminPortal.skillsProgrammes,
    element: (
      <ProtectedRoute roles={[OPTIX_ACADEMY_ADMIN_READ, SKILLS_PROGRAMS_READ]}>
        <AdminPortalSkillsProgrammes />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'Skills Programmes'},
      {label: 'Org-Scoped'},
    ]),
  },
  {
    path: routes.adminPortal.skillsProgrammeTemplates,
    element: (
      <ProtectedRoute roles={[OPTIX_ACADEMY_ADMIN_READ, TEMPLATES_READ]}>
        <AdminPortalSkillsProgrammeTemplates />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'Skills Programmes'},
      {label: 'Templates'},
    ]),
  },
  {
    path: routes.adminPortal.qualifications,
    element: (
      <ProtectedRoute roles={[OPTIX_ACADEMY_ADMIN_READ, QUALIFICATIONS_READ]}>
        <AdminPortalQualifications />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'Qualifications'},
      {label: 'Org-Scoped'},
    ]),
  },
  {
    path: routes.adminPortal.qualificationTemplates,
    element: (
      <ProtectedRoute roles={[OPTIX_ACADEMY_ADMIN_READ, TEMPLATES_READ]}>
        <AdminPortalQualificationTemplates />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'Qualifications', path: routes.adminPortal.qualifications},
      {label: 'Templates'},
    ]),
  },
  {
    path: routes.adminPortal.users,
    element: (
      <ProtectedRoute
        conditionalRoles={[
          [OPTIX_ACADEMY_ADMIN_READ, USER_READ],
          [OPTIX_ACADEMY_ADMIN_READ, LEARNERS_READ],
          [OPTIX_ACADEMY_ADMIN_READ, MODERATORS_READ],
          [OPTIX_ACADEMY_ADMIN_READ, FACILITATORS_READ],
          [OPTIX_ACADEMY_ADMIN_READ, ASSESSORS_READ],
        ]}
      >
        <AdminPortalUsers userDetailRoute={routes.adminPortal.userDetail} />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'Users'},
    ]),
  },
  {
    path: routes.adminPortal.enrollments,
    element: (
      <ProtectedRoute
        roles={[OPTIX_ACADEMY_ADMIN_READ, LEARNER_ENROLLMENTS_READ]}
      >
        <AdminPortalEnrollmentManagement />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'Enrollments'},
    ]),
  },
  {
    path: routes.adminPortal.createEnrollments,
    element: (
      <ProtectedRoute
        roles={[OPTIX_ACADEMY_ADMIN_READ, LEARNER_ENROLLMENTS_READ]}
      >
        <AdminPortalCreateEnrollments />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'Create Enrollments'},
    ]),
  },
  {
    path: routes.adminPortal.certificates,
    element: (
      <ProtectedRoute roles={[OPTIX_ACADEMY_ADMIN_READ, CERTIFICATE_READ]}>
        <AdminPortalCertificates />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'Certificates'},
    ]),
  },
  {
    path: routes.adminPortal.cohorts,
    element: (
      <ProtectedRoute roles={[OPTIX_ACADEMY_ADMIN_READ, COHORTS_READ]}>
        <AdminPortalCohortManagement />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'Cohorts'},
    ]),
  },
  {
    path: routes.adminPortal.assessments,
    element: (
      <ProtectedRoute
        roles={[OPTIX_ACADEMY_ADMIN_READ, TRAINME_OPTIX_ASSESSMENT_READ]}
      >
        <AdminPortalAssessments />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'Assessments'},
    ]),
  },
  {
    path: routes.adminPortal.assessmentCreate,
    element: (
      <ProtectedRoute
        roles={[OPTIX_ACADEMY_ADMIN_READ, TRAINME_OPTIX_ASSESSMENT_READ]}
      >
        <AdminPortalCreateAssessment />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'Assessments', path: routes.adminPortal.assessments},
      {label: 'Create'},
    ]),
  },
  {
    path: routes.adminPortal.assessmentEdit,
    element: (
      <ProtectedRoute
        roles={[OPTIX_ACADEMY_ADMIN_READ, TRAINME_OPTIX_ASSESSMENT_READ]}
      >
        <AdminPortalEditAssessment />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'Assessments', path: routes.adminPortal.assessments},
      {label: 'Edit'},
    ]),
  },
  {
    path: routes.adminPortal.assessmentTemplates,
    element: (
      <ProtectedRoute roles={[OPTIX_ACADEMY_ADMIN_READ, TEMPLATES_READ]}>
        <AdminPortalAssessmentTemplates />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'Assessments', path: routes.adminPortal.assessments},
      {label: 'Templates'},
    ]),
  },
  {
    path: routes.adminPortal.assessmentTemplateCreate,
    element: (
      <ProtectedRoute roles={[OPTIX_ACADEMY_ADMIN_READ, TEMPLATES_READ]}>
        <AdminPortalCreateAssessmentTemplate />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'Assessments', path: routes.adminPortal.assessments},
      {label: 'Templates', path: routes.adminPortal.assessmentTemplates},
      {label: 'Create'},
    ]),
  },
  {
    path: routes.adminPortal.assessmentTemplateEdit,
    element: (
      <ProtectedRoute roles={[OPTIX_ACADEMY_ADMIN_READ, TEMPLATES_READ]}>
        <AdminPortalEditAssessmentTemplate />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'Assessments', path: routes.adminPortal.assessments},
      {label: 'Templates', path: routes.adminPortal.assessmentTemplates},
      {label: 'Edit'},
    ]),
  },
  {
    path: routes.adminPortal.assessmentTemplateDetail,
    element: (
      <ProtectedRoute roles={[OPTIX_ACADEMY_ADMIN_READ, TEMPLATES_READ]}>
        <AdminPortalAssessmentTemplateDetail />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'Assessments', path: routes.adminPortal.assessments},
      {label: 'Templates', path: routes.adminPortal.assessmentTemplates},
      {label: 'Detail'},
    ]),
  },
  {
    path: routes.adminPortal.cohortDetail,
    element: (
      <ProtectedRoute roles={adminPortalRoles}>
        <AdminPortalCohortDetail />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'Cohorts', path: routes.adminPortal.cohorts},
      {label: 'Cohort Detail'},
    ]),
  },
  {
    path: routes.adminPortal.moduleManagement,
    element: (
      <ProtectedRoute roles={[OPTIX_ACADEMY_ADMIN_READ, TRAINME_COURSES_READ]}>
        <AdminPortalModuleManagement />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'Modules'},
    ]),
  },
  {
    path: routes.adminPortal.courseModules,
    element: (
      <ProtectedRoute roles={[OPTIX_ACADEMY_ADMIN_READ, TRAINME_COURSES_READ]}>
        <AdminPortalCourseModules />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'Course Modules'},
    ]),
  },
  {
    path: routes.adminPortal.userDetail,
    element: (
      <ProtectedRoute roles={[...adminPortalRoles, USER_PROFILE_SUMMARY_READ]}>
        <AdminPortalUserDetail />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'Users', path: routes.adminPortal.users},
      {label: 'User Detail'},
    ]),
  },
  {
    path: routes.adminPortal.standUpTasks,
    element: (
      <ProtectedRoute roles={adminPortalRoles}>
        <AdminPortalStandUpTasks />
      </ProtectedRoute>
    ),
    loader: breadcrumbLoaderFunction([
      ...baseAdminPortalBreadcrumbs,
      {label: 'Tasks'},
    ]),
  },
];
